import { useTranslation } from 'react-i18next';
import { Button, Panel } from '@/shared/ui';
import { GeoMap } from '@/shared/ui/map';
import type { StationFormApi } from '../useStationForm';
import type { PlaceEditor } from '../usePlaceEditor';
import { buildMarkers } from './placeUtils';

export interface StationMapPanelProps {
  form: StationFormApi;
  editor: PlaceEditor;
}

/** Panel s mapou — markery nástupních míst, mapové režimy (banner) a vystředění. */
export function StationMapPanel({ form, editor }: StationMapPanelProps) {
  const { t } = useTranslation('products');
  const { mapMode, setMapMode, fit, fitSignal, handleMapClick } = editor;

  return (
    <div className="min-h-0 flex-1">
      <Panel
        variant="filled"
        icon="map"
        title={t('map.title')}
        subtitle={
          <form.Subscribe selector={(state) => state.values.stationDetails.length}>
            {(count) => (
              <span className="text-phx-text-muted text-phx-sm">
                {t('map.placesCount', { count })}
              </span>
            )}
          </form.Subscribe>
        }
        padded={false}
        className="flex h-full min-h-0 flex-col"
        actions={
          <>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              iconLeft="pin"
              onClick={() => setMapMode({ kind: 'new-place' })}
            >
              {t('map.newPlace')}
            </Button>
            <Button type="button" variant="ghost" size="sm" onClick={fit}>
              {t('map.fit')}
            </Button>
          </>
        }
        footer={<span className="text-phx-text-muted text-phx-xs">{t('map.legend')}</span>}
      >
        <div className="relative h-full min-h-0">
          {mapMode.kind !== 'idle' ? (
            <div className="rounded-phx top-phx-3 bg-phx-text text-phx-xs px-phx-3 py-phx-1 absolute left-1/2 z-[1000] -translate-x-1/2 text-white shadow">
              {t(`map.mode.${mapMode.kind}`)}
            </div>
          ) : null}
          <form.Subscribe
            selector={(state) => ({
              details: state.values.stationDetails,
              lat: state.values.stationLatitude,
              lng: state.values.stationLongitude,
            })}
          >
            {({ details, lat, lng }) => (
              <GeoMap
                markers={buildMarkers(details, lat, lng)}
                fitSignal={fitSignal}
                onMapClick={mapMode.kind === 'idle' ? undefined : handleMapClick}
              />
            )}
          </form.Subscribe>
        </div>
      </Panel>
    </div>
  );
}
